import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Trash2, ShoppingCart, Store, Search, MapPin, AlertTriangle, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatRupiah, formatNumber } from "../lib/format";
import { Section, EmptyState } from "../components/UI";
import BIPricePanel from "../components/BIPricePanel";
import EwsTable from "../components/EwsTable";

export default function BuatPOPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  // Qty yang tercatat pada draft ini, dipakai untuk mengisi ulang keranjang.
  // Draft belum memotong stok, jadi qty tetap dibatasi stok produk saat ini.
  const [baseQty, setBaseQty] = useState({});
  const [peringatanStok, setPeringatanStok] = useState([]);
  const [draftSupplierId, setDraftSupplierId] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(isEdit);
  const [suppliers, setSuppliers] = useState([]);
  const [searchSupplier, setSearchSupplier] = useState("");
  const [hasilCari, setHasilCari] = useState([]);
  const [hargaBI, setHargaBI] = useState([]);
  const [hargaEws, setHargaEws] = useState([]);
  const [mencari, setMencari] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // { product_id: { qty, ...prod } }
  const [catatan, setCatatan] = useState("");
  const [sppg, setSppg] = useState(null);
  const [sppgLoading, setSppgLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/suppliers", { params: { verified: true } });
      setSuppliers(data);
    })();
  }, []);

  // Harga acuan PIHPS Bank Indonesia sebagai rujukan sebelum memesan.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bi-prices");
        setHargaBI(data.prices || []);
      } catch {
        setHargaBI([]);
      }
      try {
        const { data } = await api.get("/master-data/ews");
        setHargaEws(data.items || []);
      } catch {
        setHargaEws([]);
      }
    })();
  }, []);

  // Pencarian dilakukan di server supaya nama produk ikut terjangkau.
  useEffect(() => {
    const kata = searchSupplier.trim();
    if (!kata) { setHasilCari([]); setMencari(false); return undefined; }
    setMencari(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/suppliers", { params: { verified: true, q: kata } });
        setHasilCari(data);
      } catch {
        setHasilCari([]);
      } finally { setMencari(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchSupplier]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/purchase-orders/${editId}`);
        if (data.status !== "Draft PO") {
          setError(`PO ini berstatus "${data.status}" dan tidak dapat diubah lagi.`);
          setLoadingDraft(false);
          return;
        }
        const qtyMap = {};
        (data.items || []).forEach((it) => {
          qtyMap[it.product_id] = (qtyMap[it.product_id] || 0) + it.qty;
        });
        setBaseQty(qtyMap);
        setDraftSupplierId(data.supplier_id);
        setCatatan(data.catatan || "");
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoadingDraft(false);
      }
    })();
  }, [isEdit, editId]);

  useEffect(() => {
    if (!isEdit || !draftSupplierId || suppliers.length === 0) return;
    const sup = suppliers.find((x) => x.id === draftSupplierId);
    if (sup) setSelectedSupplier(sup);
  }, [isEdit, suppliers, draftSupplierId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agencies/me/profile");
        setSppg(data || null);
      } catch {
        setSppg(null);
      } finally {
        setSppgLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSupplier) { setProducts([]); return; }
    (async () => {
      const { data } = await api.get("/products", { params: { supplier_id: selectedSupplier.id } });
      const tersedia = data.filter((p) => p.status === "Aktif" && p.stok > 0);
      setProducts(tersedia);
      // Saat draft dibuka, keranjang diisi ulang dengan qty pesanan yang tersimpan.
      if (isEdit && selectedSupplier.id === draftSupplierId) {
        const awal = {};
        const catatanStok = [];
        Object.entries(baseQty).forEach(([pid, qty]) => {
          const p = tersedia.find((x) => x.id === pid);
          if (!p) {
            const nama = data.find((x) => x.id === pid)?.nama || "Salah satu produk";
            catatanStok.push(`${nama} sudah tidak tersedia dan dikeluarkan dari keranjang.`);
            return;
          }
          awal[pid] = { ...p, qty: Math.min(qty, p.stok) };
          if (qty > p.stok) catatanStok.push(`${p.nama} disesuaikan ke stok tersisa (${p.stok}).`);
        });
        setCart(awal);
        setPeringatanStok(catatanStok);
      } else {
        setCart({});
        setPeringatanStok([]);
      }
    })();
  }, [selectedSupplier, baseQty, isEdit, draftSupplierId]);

  const addToCart = (p) => {
    setCart((c) => {
      const currentQty = c[p.id]?.qty || 0;
      const newQty = Math.min(currentQty + 1, p.stok);
      return { ...c, [p.id]: { ...p, qty: newQty } };
    });
  };
  const updateQty = (id, qty) => {
    const q = Math.max(0, Number(qty) || 0);
    setCart((c) => {
      const next = { ...c };
      if (q === 0) delete next[id];
      else {
        const maxStok = next[id]?.stok || Infinity;
        next[id] = { ...next[id], qty: Math.min(q, maxStok) };
      }
      return next;
    });
  };
  const removeItem = (id) => {
    setCart((c) => { const n = { ...c }; delete n[id]; return n; });
  };

  const items = Object.values(cart);
  const total = useMemo(() => items.reduce((s, i) => s + i.qty * i.harga, 0), [items]);

  const submit = async () => {
    setError("");
    if (!selectedSupplier) { setError("Pilih supplier terlebih dahulu."); return; }
    if (items.length === 0) { setError("Tambahkan minimal 1 item ke keranjang."); return; }
    if (!sppg) { setError("Lengkapi Profil SPPG terlebih dahulu agar alamat tujuan pengiriman terisi."); return; }
    const overStock = items.find((i) => i.qty > i.stok);
    if (overStock) { setError(`Qty "${overStock.nama}" (${overStock.qty}) melebihi stok tersedia (${overStock.stok}).`); return; }
    setSubmitting(true);
    try {
      const payload = {
        supplier_id: selectedSupplier.id,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        catatan: catatan || null,
        dest_kota: sppg.kota,
        dest_lat: Number(sppg.lat),
        dest_lng: Number(sppg.lng),
      };
      if (isEdit) {
        await api.patch(`/purchase-orders/${editId}`, {
          items: payload.items,
          catatan: catatan || "",
          dest_kota: payload.dest_kota,
          dest_lat: payload.dest_lat,
          dest_lng: payload.dest_lng,
        });
        navigate(`/purchase-orders/${editId}`);
      } else {
        const { data } = await api.post("/purchase-orders", payload);
        navigate(`/purchase-orders/${data.id}`);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="buat-po-page">
      <button
        onClick={() => navigate("/purchase-orders")}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        data-testid="back-to-po-list"
      >
        <ArrowLeft className="w-4 h-4" /> Kembali ke Purchase Orders
      </button>

      <header>
        <div className="kedan-label text-slate-500">Procurement</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">
          {isEdit ? "Ubah Draft PO" : "Buat Purchase Order"}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {isEdit
            ? "Perubahan tersimpan sebagai draft. PO baru terkirim ke supplier setelah Anda menekan “Kirim ke Supplier” pada halaman detail."
            : "Pilih supplier → tentukan komoditas & jumlah → simpan sebagai draft."}
        </p>
      </header>

      <BIPricePanel prices={hargaBI} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Supplier selector + product list */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tabel status EWS mengikuti lebar kartu pemesanan di kolom ini */}
          <EwsTable items={hargaEws} />

          <div className="kedan-card p-5">
            <div className="kedan-label text-slate-500 mb-2">1. Pilih Supplier</div>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  className="kedan-input !py-1.5 !pl-9 text-sm"
                  placeholder="Cari nama supplier, kota, atau nama produk..."
                  value={searchSupplier}
                  onChange={(e) => setSearchSupplier(e.target.value)}
                  data-testid="search-supplier"
                />
              </div>
            </div>
            
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1 kedan-scrollbar">
              {suppliers.length === 0 && <div className="text-sm text-slate-500">Belum ada supplier terverifikasi.</div>}
              {suppliers.length > 0 && !searchSupplier.trim() && (
                <div className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-md border border-dashed border-slate-200">
                  Ketik nama atau kota supplier untuk mencari...
                </div>
              )}
              {searchSupplier.trim() && mencari && hasilCari.length === 0 && (
                <div className="text-sm text-slate-500 text-center py-4">Mencari...</div>
              )}
              {searchSupplier.trim() && !mencari && hasilCari.length === 0 && (
                <div className="text-sm text-slate-500 text-center py-4">Supplier tidak ditemukan.</div>
              )}
              {searchSupplier.trim() && hasilCari.map((s) => (
                <button key={s.id} onClick={() => setSelectedSupplier(s)}
                  className={`text-left p-3 border rounded-md transition-all flex items-center justify-between gap-3 ${
                    selectedSupplier?.id === s.id ? "border-[#045498] bg-blue-50/50 ring-1 ring-[#045498]" : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  data-testid={`select-supplier-${s.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-[#045498]" />
                      <div className="font-semibold text-slate-900 text-sm">{s.nama_usaha}</div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {(s.jenis_produk || []).join(", ")} · {s.kota}
                      {s.kecamatan && `, Kec. ${s.kecamatan}`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedSupplier && (
            <div className="kedan-card overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <div className="kedan-label text-slate-500">2. Pilih Komoditas</div>
                <div className="font-display font-semibold mt-0.5">{selectedSupplier.nama_usaha}</div>
              </div>
              {products.length === 0 ? (
                <EmptyState title="Belum ada produk aktif" description="Supplier ini belum memiliki produk tersedia." />
              ) : (
                <table className="kedan-table">
                  <thead><tr>
                    <th>Produk</th><th>Kategori</th>
                    <th className="text-right">Harga</th>
                    <th className="text-right">Stok</th>
                    <th></th>
                  </tr></thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id}>
                        <td className="font-medium">{p.nama}</td>
                        <td className="text-slate-500 text-xs">{p.kategori}</td>
                        <td className="text-right font-mono">{formatRupiah(p.harga)}/{p.satuan}</td>
                        <td className="text-right font-mono">{formatNumber(p.stok)}</td>
                        <td className="text-right">
                          <button onClick={() => addToCart(p)} className="kedan-btn-secondary !py-1 !px-2" data-testid={`add-cart-${p.id}`}>
                            <Plus className="w-3 h-3" /> Tambah
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Cart / submit */}
        <div className="space-y-4">
          <div className="kedan-card p-5">
            <div className="flex items-center justify-between">
              <div className="kedan-label text-slate-500">3. Keranjang</div>
              <ShoppingCart className="w-4 h-4 text-slate-400" />
            </div>
            <div className="mt-3 space-y-3" data-testid="cart-list">
              {items.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">Keranjang masih kosong.</div>}
              {items.map((i) => (
                <div key={i.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-slate-800">{i.nama}</div>
                    <button onClick={() => removeItem(i.id)} className="text-red-500 hover:text-red-700" data-testid={`remove-cart-${i.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatRupiah(i.harga)}/{i.satuan}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <input type="number" min="1" max={i.stok} value={i.qty}
                      onChange={(e) => updateQty(i.id, e.target.value)}
                      className="kedan-input !w-20 !py-1 text-center font-mono text-sm"
                      data-testid={`qty-${i.id}`} />
                    <span className="text-xs text-slate-500">/ {formatNumber(i.stok)} stok</span>
                    <span className="text-xs text-slate-500">× {formatRupiah(i.harga)} =</span>
                    <span className="text-sm font-semibold font-mono">{formatRupiah(i.qty * i.harga)}</span>
                  </div>
                  {i.qty >= i.stok && (
                    <div className="text-xs text-amber-600 mt-1">⚠ Qty mencapai batas stok tersedia</div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <div className="kedan-label text-slate-500">Total</div>
              <div className="font-display font-bold text-2xl text-[#045498]" data-testid="cart-total">{formatRupiah(total)}</div>
            </div>
          </div>

          <div className="kedan-card p-5 space-y-3">
            <div className="kedan-label text-slate-500">4. Detail Tujuan</div>

            {sppgLoading ? (
              <div className="h-20 bg-slate-100 rounded-md animate-pulse" />
            ) : sppg ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="dest-sppg">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#045498] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">{sppg.nama_instansi}</div>
                    <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                      {sppg.alamat}
                      {sppg.kelurahan && <>, Kel. {sppg.kelurahan}</>}
                      {sppg.kecamatan && <>, Kec. {sppg.kecamatan}</>}
                      <br />
                      {sppg.kota}{sppg.provinsi && `, ${sppg.provinsi}`}
                    </div>
                    {sppg.no_telp && <div className="text-xs text-slate-500 mt-1">Telp. {sppg.no_telp}</div>}
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500">
                  Alamat diambil dari{" "}
                  <Link to="/profil-sppg" className="text-[#045498] hover:underline font-medium">Profil SPPG</Link>.
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="dest-sppg-empty">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-800 leading-relaxed">
                  Profil SPPG belum diisi, sehingga alamat tujuan pengiriman belum tersedia.{" "}
                  <Link to="/profil-sppg" className="font-semibold underline">Lengkapi Profil SPPG</Link> terlebih dahulu.
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Catatan Pesanan</label>
              <textarea rows="2" className="kedan-input" value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="po-catatan" />
            </div>
          </div>

          {peringatanStok.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-md px-3 py-2 space-y-1" data-testid="peringatan-stok">
              {peringatanStok.map((t, i) => <div key={i}>{t}</div>)}
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2" data-testid="buat-po-error">{error}</div>}

          <button onClick={submit} disabled={submitting || loadingDraft || items.length === 0 || !selectedSupplier || !sppg}
            className="kedan-btn-primary w-full !py-2.5" data-testid="submit-po-btn">
            {submitting ? "Menyimpan..." : isEdit ? "Simpan Perubahan Draft" : "Simpan sebagai Draft PO"}
          </button>
          <p className="text-xs text-slate-500 text-center">
            PO tersimpan sebagai <span className="font-medium">Draft PO</span> dan belum terlihat oleh supplier.
            Stok supplier baru terpotong saat PO dikirim.
          </p>
        </div>
      </div>
    </div>
  );
}
